import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, type LucideIcon, type LucideProps } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { dirGlyph, directionForLanguage, type DirGlyph, type DirIconName, type TextDirection } from './dirIconModel';

const GLYPHS: Record<DirGlyph, LucideIcon> = {
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
};

export interface DirIconProps extends Omit<LucideProps, 'ref'> {
  /** Logical name: `next` / `prev` (chevrons), `forward` / `back` (arrows). */
  name: DirIconName;
  /** Force a direction (rare: an LTR island). Default: the active i18n language, then `<html dir>`. */
  dir?: TextDirection;
}

function documentDirection(): TextDirection {
  if (typeof document === 'undefined') return 'rtl';
  return document.documentElement.dir === 'ltr' ? 'ltr' : 'rtl';
}

/**
 * Direction-aware chevron / arrow (DESIGN_SYSTEM.md §18). Use it instead of a physical
 * `ChevronLeft` / `ArrowRight` wherever the icon means "next", "previous", "forward" or "back",
 * so it flips with the language. Decorative by default (`aria-hidden`): the button or link around
 * it carries the accessible name. Pass `aria-label` only when the icon is the sole content
 * and no wrapper names it.
 *
 * Icons that do not mean a reading direction (play, external link, a chart axis) stay physical.
 */
export default function DirIcon({ name, dir, 'aria-hidden': ariaHidden, 'aria-label': ariaLabel, ...rest }: DirIconProps) {
  const { i18n } = useTranslation();
  const direction = dir ?? (i18n.language ? directionForLanguage(i18n.language) : documentDirection());
  const Glyph = GLYPHS[dirGlyph(name, direction)];
  const hidden = ariaHidden ?? (ariaLabel ? undefined : true);
  return <Glyph aria-hidden={hidden} aria-label={ariaLabel} role={ariaLabel ? 'img' : undefined} focusable="false" {...rest} />;
}
